import { useState } from 'react'
import { Check, Plus, RotateCcw, Trash2, TriangleAlert } from 'lucide-react'
import { useRole } from '@/app/role'
import { useCurrentSite } from '@/app/sites'
import { PageHeader } from '@/app/shell/PageHeader'
import {
  useCreateObligation,
  useDeleteObligation,
  useObligations,
  useUpdateObligation,
} from '@/lib/api/hooks'
import { tok } from '@/charts'
import { Button } from '@/ui/button'
import { Card, KLabel } from '@/ui/card'
import { RiskChip } from '@/ui/chip'
import { PackBadge } from '@/ui/pack-badge'
import { EmptyState, LoadingBlock } from '@/ui/states'

type Severity = 'low' | 'medium' | 'high' | 'critical'
const SEVERITIES: Severity[] = ['low', 'medium', 'high', 'critical']
const SEV_TONE: Record<string, string> = {
  low: tok('--stable'),
  medium: tok('--ember'),
  high: tok('--flare'),
  critical: tok('--ignition'),
}
const isOverdue = (due?: string | null, status?: string) =>
  !!due && status === 'open' && new Date(due) < new Date(new Date().toDateString())

export function ObligationsPage() {
  const { role } = useRole()
  const { site, siteId } = useCurrentSite()
  const id = siteId ?? ''
  const obligations = useObligations(id)
  const create = useCreateObligation(id)
  const update = useUpdateObligation(id)
  const del = useDeleteObligation(id)
  const canEdit = role !== 'insurer'

  const [title, setTitle] = useState('')
  const [severity, setSeverity] = useState<Severity>('high')
  const [due, setDue] = useState('')

  const rows = obligations.data ?? []
  const openCount = rows.filter((o) => o.status === 'open').length
  const raise = () => {
    if (!title.trim()) return
    create.mutate(
      { title: title.trim(), severity, due_date: due || undefined },
      { onSuccess: () => { setTitle(''); setDue('') } },
    )
  }

  return (
    <>
      <PageHeader
        title="Obligations"
        subtitle={
          <>
            {site && <PackBadge pack={site.pack} />}
            <span>{openCount} open</span>
            <span className="text-faint">·</span>
            <span>{rows.length} total</span>
          </>
        }
      />

      {canEdit && (
        <Card className="mb-4">
          <KLabel className="mb-3">Raise an obligation</KLabel>
          <div className="flex flex-wrap items-end gap-3">
            <div className="min-w-[240px] flex-1">
              <label className="mb-1 block text-[12px] font-medium">Title</label>
              <input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && raise()}
                placeholder="e.g. Recalibrate hall gas detectors"
                className="h-9 w-full rounded-md border border-input bg-surface-2 px-3 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-ring"
              />
            </div>
            <div>
              <label className="mb-1 block text-[12px] font-medium">Severity</label>
              <select
                value={severity}
                onChange={(e) => setSeverity(e.target.value as Severity)}
                className="h-9 rounded-md border border-input bg-surface-2 px-2 text-sm capitalize outline-none focus:border-primary"
              >
                {SEVERITIES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-[12px] font-medium">Due date</label>
              <input
                type="date"
                value={due}
                onChange={(e) => setDue(e.target.value)}
                className="h-9 rounded-md border border-input bg-surface-2 px-2 text-sm outline-none focus:border-primary"
              />
            </div>
            <Button onClick={raise} disabled={!title.trim() || create.isPending}>
              <Plus /> {create.isPending ? 'Raising…' : 'Raise'}
            </Button>
          </div>
        </Card>
      )}

      {obligations.isLoading ? (
        <Card>
          <LoadingBlock lines={4} />
        </Card>
      ) : rows.length === 0 ? (
        <EmptyState kind="no-data" title="No obligations">
          Obligations track remediation actions — high-severity or overdue ones feed the critical-risk flag.
        </EmptyState>
      ) : (
        <Card className="overflow-hidden p-0">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-wider text-faint">
                <th className="px-4 py-3 font-semibold">Obligation</th>
                <th className="px-4 py-3 font-semibold">Severity</th>
                <th className="px-4 py-3 font-semibold">Due</th>
                <th className="px-4 py-3 font-semibold">Status</th>
                {canEdit && <th className="px-4 py-3" />}
              </tr>
            </thead>
            <tbody>
              {rows.map((o) => {
                const overdue = isOverdue(o.due_date, o.status)
                const busy =
                  (update.isPending && update.variables?.obligationId === o.obligation_id) ||
                  (del.isPending && del.variables === o.obligation_id)
                return (
                  <tr key={o.obligation_id} className="border-t">
                    <td className="px-4 py-2.5 font-medium">{o.title}</td>
                    <td className="px-4 py-2.5">
                      <RiskChip label={o.severity} tone={SEV_TONE[o.severity] ?? tok('--muted-foreground')} />
                    </td>
                    <td className="px-4 py-2.5">
                      {o.due_date ? (
                        <span className={overdue ? 'inline-flex items-center gap-1 font-medium text-ignition' : 'text-muted-foreground'}>
                          {overdue && <TriangleAlert className="size-3.5" />}
                          {o.due_date}
                        </span>
                      ) : (
                        <span className="text-faint">—</span>
                      )}
                    </td>
                    <td className="px-4 py-2.5">
                      <span className={o.status === 'open' ? 'text-foreground' : 'text-faint'}>{o.status}</span>
                    </td>
                    {canEdit && (
                      <td className="px-4 py-2.5">
                        <div className="flex justify-end gap-1">
                          {o.status === 'open' ? (
                            <Button
                              size="sm"
                              variant="ghost"
                              disabled={busy}
                              onClick={() => update.mutate({ obligationId: o.obligation_id, patch: { status: 'closed' } })}
                            >
                              <Check /> Close
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              variant="ghost"
                              disabled={busy}
                              onClick={() => update.mutate({ obligationId: o.obligation_id, patch: { status: 'open' } })}
                            >
                              <RotateCcw /> Reopen
                            </Button>
                          )}
                          <Button size="sm" variant="ghost" disabled={busy} onClick={() => del.mutate(o.obligation_id)}>
                            <Trash2 />
                          </Button>
                        </div>
                      </td>
                    )}
                  </tr>
                )
              })}
            </tbody>
          </table>
        </Card>
      )}
    </>
  )
}
